/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CommandBarButton } from '@fluentui/react/lib/Button';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { TableCommandBarProps } from 'src/core/components/TableCommandBarProps';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getCommandBarButtonStyles } from 'src/core/utils/azureRbacStyles';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { commandBarItemSelectionManager } from 'src/core/utils/commandBarItemSelectionManager';
import { BatchTest } from 'src/luis/projects/testPane/types/BatchTest';

export type BatchTestTableCommandBarProps = TableCommandBarProps<BatchTest> & {
    /**
     * Callback for importing a batch test.
     */
    onImportBatchTest: () => void;

    /**
     * Callback for running a batch test.
     */
    onRunBatchTest: () => void;

    /**
     * Callback for renaming an batch test.
     */
    onRenameBatchTest: () => void;

    /**
     * Callback for downloading a batch test.
     */
    onDownloadBatchTest: () => void;

    /**
     * Callback for deleting batch tests.
     */
    onDeleteBatchTests: () => void;

    /**
     * User role to extract actions available for user.
     */
    userRole: string;

    /**
     * User role to show in luis tool tip.
     */
    displayedUserRole: string;
};

const messages = defineMessages({
    importBatchTestButton: {
        id: 'testPane.batchTestTableCommandBar.importBatchTestButton',
        defaultMessage: 'Import'
    },
    runBatchTestButton: {
        id: 'testPane.batchTestTableCommandBar.runBatchTestButton',
        defaultMessage: 'Run'
    },
    renameBatchTestButton: {
        id: 'testPane.batchTestTableCommandBar.renameBatchTestButton',
        defaultMessage: 'Rename'
    },
    downloadBatchTestButton: {
        id: 'testPane.batchTestTableCommandBar.downloadBatchTestButton',
        defaultMessage: 'Download'
    },
    deleteBatchTestButton: {
        id: 'testPane.batchTestTableCommandBar.deleteBatchTestButton',
        defaultMessage: 'Delete'
    }
});

/**
 * Represents a command bar for the dataset table.
 *
 * @param props The properties for the dataset table command bar.
 */
export const BatchTestTableCommandBar = (props: BatchTestTableCommandBarProps) => {
    const { selectedItems = [], userRole, displayedUserRole } = props;
    const { localize } = useLocalization();

    const { canWrite, canDeleteData } = mapUserRoleToUserActions(userRole);

    const commands: ICommandBarItemProps[] = [
        { key: 'import', name: localize(messages.importBatchTestButton), iconProps: { iconName: 'Add' }, onClick: props.onImportBatchTest },
        { key: 'run', name: localize(messages.runBatchTestButton), iconProps: { iconName: 'Play' }, onClick: props.onRunBatchTest },
        {
            key: 'rename',
            onRender: item => (
                <RBACTooltip
                    hidden={canWrite || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="renameButtonLuisToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            disabled={!canWrite || item.disabled}
                            onClick={props.onRenameBatchTest}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            iconProps={{ iconName: 'Rename' }}
                            data-automation-id="renameButton"
                            text={localize(messages.renameBatchTestButton)}
                            name={localize(messages.renameBatchTestButton)}
                            ariaLabel={localize(messages.renameBatchTestButton)}
                        />
                    }
                />
            )
        },
        {
            key: 'download',
            name: localize(messages.downloadBatchTestButton),
            iconProps: { iconName: 'Download' },
            onClick: props.onDownloadBatchTest
        },
        {
            key: 'delete',
            onRender: item => (
                <RBACTooltip
                    hidden={canDeleteData || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="deleteButtonLuisToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            disabled={!canDeleteData || item.disabled}
                            onClick={props.onDeleteBatchTests}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            iconProps={{ iconName: 'Delete' }}
                            data-automation-id="deleteButton"
                            text={localize(messages.deleteBatchTestButton)}
                            name={localize(messages.deleteBatchTestButton)}
                            ariaLabel={localize(messages.deleteBatchTestButton)}
                        />
                    }
                />
            )
        }
    ];

    const items: ICommandBarItemProps[] = commandBarItemSelectionManager(
        commands,
        ['import'],
        ['run', 'rename', 'download', 'delete'],
        ['delete'],
        selectedItems.length
    );

    return <LuisCommandBar items={items} />;
};
